import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Gift, ArrowRight, Loader2, X, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { apiFetch } from "@/lib/api-fetch";

type BillingInterval = "monthly" | "yearly";

interface GiftModalProps {
  show: boolean;
  onDismiss: () => void;
}

export function GiftModal({ show, onDismiss }: GiftModalProps) {
  const [recipientEmail, setRecipientEmail] = useState("");
  const [plan, setPlan] = useState<BillingInterval>("monthly");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { toast } = useToast();

  const baseUrl = import.meta.env.BASE_URL.replace(/\/$/, "");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const res = await apiFetch(`${baseUrl}/api/payments/create-gift-checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ recipientEmail: recipientEmail.trim(), plan }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || "Failed to start gift checkout");
        return;
      }

      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      }
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setRecipientEmail("");
    setError("");
    onDismiss();
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          key="gift-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)" }}
          onClick={handleClose}
        >
          <motion.div
            key="gift-panel"
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: "spring", stiffness: 300, damping: 28 }}
            className="w-full max-w-md bg-card border border-border rounded-2xl shadow-2xl p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="text-center mb-6">
              <div className="text-4xl mb-3">🎁</div>
              <h2 className="font-display font-bold text-xl mb-1">Gift a Membership</h2>
              <p className="text-sm text-muted-foreground">
                Give someone you know the gift of writing in their own voice.
              </p>
            </div>

            {error && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-4 p-3 rounded-xl bg-destructive/10 border border-destructive/20 text-sm text-destructive"
              >
                {error}
              </motion.div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-sm font-medium" htmlFor="gift-email">
                  Recipient's email
                </label>
                <Input
                  id="gift-email"
                  type="email"
                  placeholder="friend@email.com"
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  className="h-11 rounded-xl bg-background border-border"
                  autoFocus
                  required
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium">Plan</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setPlan("monthly")}
                    className={`flex flex-col items-center p-3 rounded-xl border transition-all ${
                      plan === "monthly"
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border hover:border-border/80 text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <div className="flex items-center gap-1.5 mb-0.5">
                      {plan === "monthly" && <Check className="w-3.5 h-3.5" />}
                      <span className="text-sm font-semibold">Monthly</span>
                    </div>
                    <span className="text-xl font-bold">$8</span>
                    <span className="text-xs opacity-70">per month</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setPlan("yearly")}
                    className={`flex flex-col items-center p-3 rounded-xl border transition-all relative overflow-hidden ${
                      plan === "yearly"
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border hover:border-border/80 text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <span className="absolute top-1.5 right-1.5 text-[9px] font-bold px-1 py-0.5 rounded bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                      SAVE 20%
                    </span>
                    <div className="flex items-center gap-1.5 mb-0.5">
                      {plan === "yearly" && <Check className="w-3.5 h-3.5" />}
                      <span className="text-sm font-semibold">Annual</span>
                    </div>
                    <span className="text-xl font-bold">$76.80</span>
                    <span className="text-xs opacity-70">per year</span>
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-12 rounded-xl font-bold"
                disabled={loading || !recipientEmail.trim()}
                style={{
                  background: "linear-gradient(135deg, hsl(188 90% 42%) 0%, hsl(205 88% 52%) 100%)",
                }}
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Gift className="w-4 h-4 mr-2" />
                    Continue to Payment
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>

              <p className="text-xs text-center text-muted-foreground">
                The recipient will receive a branded email with a redemption link.
              </p>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
